export interface FetchRetryEvent {
  attempt: number;
  maxRetries: number;
  response?: Response;
  error?: Error;
}

export interface FetchDriverRequest {
  url: string;
  init: RequestInit;
  timeoutMs: number;
  maxRetries: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  shouldRetryResponse?: (response: Response) => boolean;
  shouldRetryError?: (error: Error) => boolean;
  beforeRetry?: (event: FetchRetryEvent) => Promise<void> | void;
}

export class FetchTimeoutError extends Error {
  constructor(url: string, method: string, timeoutMs: number, cause?: unknown) {
    super(`${method} ${url} timed out after ${timeoutMs}ms`);
    this.name = 'TimeoutError';
    this.cause = cause;
  }
}

export function resolveFetchImpl(fetchImpl?: typeof fetch): typeof fetch {
  const resolved =
    fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
  if (!resolved) {
    throw new Error('fetch is not available');
  }
  return resolved;
}

export function createAbortError(message = 'Aborted'): Error {
  if (typeof DOMException === 'function') {
    return new DOMException(message, 'AbortError');
  }
  return Object.assign(new Error(message), { name: 'AbortError' });
}

export function delay(
  ms: number,
  signal?: AbortSignal,
  abortMessage?: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError(abortMessage));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(createAbortError(abortMessage));
      },
      { once: true }
    );
  });
}

export async function waitForOnline(
  maxWaitMs: number,
  signal?: AbortSignal
): Promise<void> {
  if (
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    navigator.onLine !== false ||
    signal?.aborted
  ) {
    return;
  }
  await new Promise<void>((resolve) => {
    const cleanup = () => {
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
      clearTimeout(timer);
    };
    const finish = () => {
      cleanup();
      resolve();
    };
    const onOnline = finish;
    const onAbort = finish;
    const timer = setTimeout(finish, maxWaitMs);
    window.addEventListener('online', onOnline, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export function drainResponseBody(response: Response): void {
  try {
    if (response.bodyUsed) return;
    if (response.body && typeof response.body.cancel === 'function') {
      response.body.cancel().catch(() => undefined);
    } else {
      response.text().catch(() => undefined);
    }
  } catch {
    // Some environments throw on partial-read body inspection.
  }
}

async function fetchWithTimeout(
  request: FetchDriverRequest
): Promise<Response> {
  const fetchImpl = resolveFetchImpl(request.fetchImpl);
  const method = request.init.method ?? 'GET';
  const abort = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    abort.abort();
  }, request.timeoutMs);
  const onExternalAbort = (): void => abort.abort();
  request.signal?.addEventListener('abort', onExternalAbort, { once: true });

  try {
    if (request.signal?.aborted) throw createAbortError();
    return await fetchImpl(request.url, {
      ...request.init,
      signal: abort.signal,
    });
  } catch (error) {
    if (timedOut && !request.signal?.aborted) {
      throw new FetchTimeoutError(
        request.url,
        String(method),
        request.timeoutMs,
        error
      );
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    request.signal?.removeEventListener('abort', onExternalAbort);
  }
}

export async function fetchWithRetry(
  request: FetchDriverRequest
): Promise<Response> {
  for (let attempt = 0; attempt <= request.maxRetries; attempt += 1) {
    try {
      const response = await fetchWithTimeout(request);
      if (
        attempt < request.maxRetries &&
        request.shouldRetryResponse?.(response)
      ) {
        drainResponseBody(response);
        await request.beforeRetry?.({
          attempt: attempt + 1,
          maxRetries: request.maxRetries,
          response,
        });
        continue;
      }
      return response;
    } catch (error) {
      if (request.signal?.aborted) throw error;
      if (
        attempt >= request.maxRetries ||
        !request.shouldRetryError?.(error as Error)
      ) {
        throw error;
      }
      await request.beforeRetry?.({
        attempt: attempt + 1,
        maxRetries: request.maxRetries,
        error: error as Error,
      });
    }
  }
  throw new Error('fetch retry loop exhausted');
}
